// frontend/src/components/projects/InviteByEmailCard.tsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Divider,
  Group,
  Stack,
  TagsInput,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { usePostMultiplacerProjectsInviteByEmailMutation } from "../../openapi"; // <-- adjust path
import type { IModuleAccessLevel, IMultiplacerModuleKey } from "@multiconsult-group/multiplacer-core";
import { useAppSelector } from "../app/hooks";
import { selectProjectNo } from "../globalSelectors";
import { selectVisibleModulesForCurrentProject } from "../projectSelectors";
import { ModulePermissionsMatrix } from "./ModulePermissionsMatrix";

type ModulePermissionRow = {
  module: IMultiplacerModuleKey;
  access: IModuleAccessLevel;
};

export const InviteByEmailCard: React.FC = () => {
  const projectNo = useAppSelector(selectProjectNo);
  const visibleModules = useAppSelector(selectVisibleModulesForCurrentProject);

  const [email, setEmail] = useState("");
  const [buildingIds, setBuildingIds] = useState<string[]>([]);

  const moduleKeys = useMemo(
    () => visibleModules.map((m) => m.key as IMultiplacerModuleKey),
    [visibleModules]
  );

  // Minimal state: store chosen access per module (READ/WRITE/none)
  const [accessByModule, setAccessByModule] = useState<Record<string, IModuleAccessLevel | "NONE">>(
    () => {
      const init: Record<string, IModuleAccessLevel | "NONE"> = {};
      for (const k of moduleKeys) init[k] = "NONE";
      init["CEILING"] = "READ";
      return init;
    }
  );

  const [inviteByEmail, { isLoading, isError, error, isSuccess, data }] =
    usePostMultiplacerProjectsInviteByEmailMutation();

  useEffect(() => {
    setAccessByModule((prev) => {
      const next: Record<string, IModuleAccessLevel | "NONE"> = {};

      for (const key of moduleKeys) {
        next[key] = prev[key] ?? "NONE";
      }

      if ("CEILING" in next && next.CEILING === "NONE") {
        next.CEILING = "READ";
      }

      return next;
    });
  }, [moduleKeys]);

  const canSubmit = useMemo(() => {
    const e = email.trim().toLowerCase();
    const hasEmail = e.includes("@") && e.includes(".");
    const hasProject = Boolean(projectNo);
    const hasAnyModule = Object.values(accessByModule).some((v) => v !== "NONE");
    return hasEmail && hasProject && hasAnyModule;
  }, [email, projectNo, accessByModule]);

  const errorText = useMemo(() => {
    const anyErr: any = error;
    return (
      anyErr?.data?.message ||
      anyErr?.error ||
      anyErr?.message ||
      (isError ? "Kunne ikke opprette invitasjon." : null)
    );
  }, [error, isError]);

  const successText = useMemo(() => {
    if (!isSuccess) return null;
    const inviteEmail = (data as any)?.inviteEmail;
    return inviteEmail ? `Invitasjon opprettet for ${inviteEmail}.` : "Invitasjon opprettet.";
  }, [data, isSuccess]);

  const onSubmit = async () => {
    if (!projectNo) return;

    const emailLower = email.trim().toLowerCase();

    const modules: ModulePermissionRow[] = Object.entries(accessByModule)
      .filter(([, v]) => v !== "NONE")
      .map(([module, access]) => ({
        module: module as IMultiplacerModuleKey,
        access: access as IModuleAccessLevel,
      }));

    await inviteByEmail({
      body: {
        projectNo,
        email: emailLower,
        permissions: {
          buildingIds,
          modules,
        },
      },
    }).unwrap();

    setEmail("");
    setBuildingIds([]);

    // reset
    setAccessByModule((prev) => {
      const next: Record<string, IModuleAccessLevel | "NONE"> = {};
      for (const k of Object.keys(prev)) next[k] = "NONE";
      next["CEILING"] = "READ";
      return next;
    });
  };

  return (
    <Card withBorder radius="md" p="md">
      <Stack gap="sm">
        <Group justify="space-between" align="center">
          <Title order={4}>Inviter bruker</Title>
          <Text size="sm" c="dimmed">
            Prosjekt {projectNo}
          </Text>
        </Group>

        <Text size="sm" c="dimmed">
          Kun gjestebrukere kan inviteres. Systemet sjekker at brukeren finnes som gjest i
          Multiconsult-tenant.
        </Text>

        {errorText ? <Alert color="red">{errorText}</Alert> : null}
        {successText ? <Alert color="green">{successText}</Alert> : null}

        <TextInput
          label="E-post"
          placeholder="navn@domene.no"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          autoComplete="email"
        />

        <Divider label="Tilgang" />

        <TagsInput
          label="Bygg (ID-er)"
          description="Valgfritt. Hvis tomt, får brukeren ingen byggtilgang."
          placeholder="Skriv inn bygg-ID og trykk Enter"
          value={buildingIds}
          onChange={setBuildingIds}
          clearable
        />

        <ModulePermissionsMatrix accessByModule={accessByModule} onChange={setAccessByModule} />

        <Group justify="flex-end">
          <Button onClick={onSubmit} loading={isLoading} disabled={!canSubmit}>
            Opprett invitasjon
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          Tips: Brukeren må først være lagt til som gjest i Entra ID (B2B). Hvis ikke, vil
          invitasjonen bli avvist.
        </Text>
      </Stack>
    </Card>
  );
};
